import { useCallback, useEffect, useState } from "react";
import { Clock, Copy, Smartphone, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, adminApi } from "@/lib/api";

export function ClockAppCard({ token }) {
  const [hasPin, setHasPin] = useState(false);
  const [pin, setPin] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);
  const [devices, setDevices] = useState([]);
  const [url, setUrl] = useState(`${window.location.origin}/clockin`);

  const load = useCallback(async () => {
    try {
      const [s, d, u] = await Promise.all([
        adminApi(token).get("/admin/status"),
        adminApi(token).get("/admin/clock-devices"),
        api.get("/public-urls").catch(() => null),
      ]);
      setHasPin(!!s.data.has_clock_pin);
      setDevices(d.data.devices || []);
      if (u?.data?.clock_in?.startsWith("http")) setUrl(u.data.clock_in);
    } catch {}
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    if (!/^\d{4,8}$/.test(pin)) return toast.error("Clock PIN must be 4–8 digits");
    if (pin !== confirm) return toast.error("PINs don't match");
    setSaving(true);
    try {
      await adminApi(token).post("/admin/clock-pin", { pin });
      toast.success("Clock PIN saved");
      setPin(""); setConfirm(""); setShow(false); setHasPin(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save");
    } finally {
      setSaving(false);
    }
  };

  const revoke = async (id) => {
    if (!window.confirm("Sign this device out of the clock app? It will need the PIN again.")) return;
    try {
      await adminApi(token).delete(`/admin/clock-devices/${id}`);
      toast.success("Device signed out");
      load();
    } catch {
      toast.error("Couldn't sign out device");
    }
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(url); toast.success("Link copied"); } catch { toast.error("Copy failed"); }
  };

  return (
    <div className="bg-white border border-stone-200 rounded-2xl p-5 space-y-4" data-testid="clock-app-card">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="font-black text-stone-800 text-sm uppercase tracking-widest flex items-center gap-2">
            <Clock className="w-4 h-4" /> Staff Clock-In App
          </div>
          <div className="text-[11px] text-stone-500 mt-1">
            A stand-alone clock at <b>{url.replace(/^https?:\/\//, "")}</b> that installs to a phone or tablet home screen. Unlock a device once with the
            clock PIN below (admin or cashier passcode also work), then staff punch with their own 4-digit PIN.
            {hasPin ? " · Clock PIN is set." : " · No clock PIN yet — admin/cashier passcode unlocks devices for now."}
          </div>
        </div>
        {!show && (
          <button type="button" onClick={() => setShow(true)} data-testid="clock-pin-change-btn"
            className="bg-brand-red text-white px-3 py-1.5 rounded-full text-[10px] uppercase tracking-widest font-bold hover:bg-red-800 shrink-0">
            {hasPin ? "Change PIN" : "Set PIN"}
          </button>
        )}
      </div>

      {show && (
        <form onSubmit={save} className="space-y-3">
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">Clock PIN (4–8 digits, not an employee PIN)</span>
            <input type="password" inputMode="numeric" pattern="\d*" required value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
              data-testid="clock-pin-new" className="mt-1 w-full px-3 py-2 rounded-lg border border-stone-300 bg-white outline-none focus:border-brand-red text-sm" />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">Confirm clock PIN</span>
            <input type="password" inputMode="numeric" pattern="\d*" required value={confirm} onChange={(e) => setConfirm(e.target.value.replace(/\D/g, ""))}
              data-testid="clock-pin-confirm" className="mt-1 w-full px-3 py-2 rounded-lg border border-stone-300 bg-white outline-none focus:border-brand-red text-sm" />
          </label>
          <div className="flex gap-2">
            <button type="submit" disabled={saving} data-testid="clock-pin-save"
              className="bg-stone-900 text-white px-4 py-2 rounded-full text-[10px] uppercase tracking-widest font-bold disabled:opacity-50">
              {saving ? "Saving…" : "Save PIN"}
            </button>
            <button type="button" onClick={() => setShow(false)} className="text-[10px] uppercase tracking-widest font-bold text-stone-500 px-3">Cancel</button>
          </div>
        </form>
      )}

      <div className="bg-stone-50 border border-stone-200 rounded-xl p-3 space-y-2">
        <div className="text-[10px] uppercase tracking-widest text-stone-500 font-bold flex items-center gap-1"><Smartphone className="w-3.5 h-3.5" /> Install on a device</div>
        <div className="flex items-center gap-2">
          <code className="text-xs bg-white border border-stone-200 rounded-lg px-2 py-1 flex-1 truncate" data-testid="clock-install-url">{url}</code>
          <button type="button" onClick={copy} data-testid="clock-copy-link" className="p-2 rounded-lg border border-stone-300 hover:bg-stone-100" aria-label="Copy link"><Copy className="w-4 h-4" /></button>
        </div>
        <ol className="text-[11px] text-stone-600 list-decimal pl-4 space-y-0.5">
          <li>Open the link on the phone/tablet and enter the clock PIN.</li>
          <li><b>iPhone/iPad:</b> Share → <i>Add to Home Screen</i>. <b>Android:</b> menu ⋮ → <i>Install app</i>.</li>
          <li>Launch “BK Clock” from the home screen — it stays unlocked on that device.</li>
        </ol>
        <div className="border-t border-stone-200 pt-2 mt-1">
          <div className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">Staff “My Hours” app</div>
          <div className="text-[11px] text-stone-600 mt-0.5">
            Each employee can install <code className="bg-white border border-stone-200 rounded px-1" data-testid="myhours-install-url">{url.replace(/\/clockin$/, "/myhours")}</code> and
            sign in with their PIN + last 4 of their phone to see only their own hours and estimated pay. Add each person&apos;s phone number in the Staff list above.
          </div>
        </div>
      </div>

      <div>
        <div className="text-[10px] uppercase tracking-widest text-stone-500 font-bold mb-1">Unlocked devices ({devices.length})</div>
        {devices.length === 0 ? (
          <div className="text-xs text-stone-400 italic" data-testid="clock-devices-empty">No devices unlocked yet.</div>
        ) : (
          <ul className="space-y-1.5" data-testid="clock-devices-list">
            {devices.map((d) => (
              <li key={d.id} className="flex items-center justify-between gap-2 text-xs bg-stone-50 border border-stone-200 rounded-lg px-3 py-2">
                <div className="min-w-0">
                  <div className="font-bold text-stone-700 truncate">{d.label || "Device"}</div>
                  <div className="text-stone-400">last used {d.last_seen ? new Date(d.last_seen).toLocaleString() : "—"}</div>
                </div>
                <button type="button" onClick={() => revoke(d.id)} data-testid={`clock-device-revoke-${d.id}`} className="p-1.5 text-stone-400 hover:text-red-600" aria-label="Sign out device"><Trash2 className="w-4 h-4" /></button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
